/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';

import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import iconIndicatorStyles, { themeIconIndicatorStyles } from './IconIndicator.styles';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    dataKey: string;
    activeIconName: string;
    inactiveIconName: string;
    _data?: object;
    altText?: string;
    functionToTransform?: string;
    isTransformData?: boolean;
    noDataMessage: string;
    typographyClass?: string;
}

interface WidgetProps extends commonTypes.WidgetInstanceProps {
    activeIconName?: string;
    inactiveIconName?: string;
    _data?: object;
    altText?: string;
    title?: string;
}

const IconIndicatorWidget = (props: WidgetProps) => {
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        dataKey,
        activeIconName,
        inactiveIconName,
        altText,
        functionToTransform,
        isTransformData,
        noDataMessage,
    } = widgetProperties || props;
    const { data, _data, title } = props;

    const i18n = commonHooks.useLocalization();

    let indicatorValue: any;

    if (_data || dataKey) {
        indicatorValue = _data || JsonQuery(dataKey as string, { data });
    } else {
        indicatorValue = { value: 2.5 };
    }

    const theme = data?.themeSelected || props?.theme;

    const acivePercentage = (parseFloat(indicatorValue?.value) / 5) * 100;
    const activePercentageRounded = `${Math.round(acivePercentage / 10) * 10}%`;
    let isRender = {
        altText,
    };

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToTransform}`)();
        const transformedData = isTransformData && transformFunction({ data });

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const _altText = i18n.format(isRender?.altText || '', data) || '';

    return (
        <>
            {indicatorValue?.value ? (
                <div
                    className={`icon-indicator ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${!theme
                            ? iconIndicatorStyles
                            : themeIconIndicatorStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles}
                    `}
                >
                    <img className="sr-only" alt={_altText} aria-label={_altText} />
                    <div className="inactive-icons" title={title} data-testid={'inactive-icons'}>
                        {Array.from(Array(5).keys()).map((i) => (
                            <i className={inactiveIconName} key={i}></i>
                        ))}
                        <div
                            className="active-icons"
                            data-testid={'active-icons'}
                            style={{ width: activePercentageRounded }}
                        >
                            {Array.from(Array(5).keys()).map((i) => (
                                <i className={activeIconName} key={i}></i>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <p
                    className="new-fund-data-message"
                    dangerouslySetInnerHTML={{ __html: i18n.format(noDataMessage, data) }}
                />
            )}
        </>
    );
};

export default IconIndicatorWidget;
